import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { Container, Row, Col } from 'react-bootstrap';
import {
  OATextField,
  OADatePickerField
} from '../../../components/fields';
import OAButton from '../../../components/widgets/OAButton';
import * as Yup from 'yup';
import { useContext, useState } from 'react';
import LedgerContext from '../context/LedgerContext';
import { getFormattedDate } from '../../../helper';
import { OA_DATE_FORMAT_BE } from '../../../constants';

interface ITopupSettlementFormModal {
  onHide: () => void;
  onSuccess?: () => void;
}

const init = {
  entityId: '',
  transactionEntity: '',
  currency: 'INR',
  entityCreatedAt: '',
  amount: '',
  transfersNotesBank: '',
  transfersNotesBranch: '',
  paymentsNotesGateway: '',
  paymentsNotesMode: '',
  settlementId: '',
  settledAt: '',
  settlementUtr: '',
  arn: '',
  additionalUtr: '',
  paymentMethod: ''
};

const TopupSettlementSchema = Yup.object().shape({
  entityId: Yup.string().required('Entity ID is required'),
  transactionEntity: Yup.string().required('Transaction Entity is required'),
  entityCreatedAt: Yup.string().required('Entity Created At is required'),
  amount: Yup.number().positive().required('Please enter amount').min(1)
});

export default function AddTopupSettlementForm(props: ITopupSettlementFormModal) {
  const { addTopupSettlement } = useContext(LedgerContext);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = (values: any, actions: FormikHelpers<any>) => {
    setLoading(true);

    // Format the request body according to API spec
    const requestBody = {
      entityId: values.entityId,
      transactionEntity: values.transactionEntity,
      currency: "INR",
      entityCreatedAt: getFormattedDate(values.entityCreatedAt, OA_DATE_FORMAT_BE),
      amount: parseFloat(values.amount),
      transfersNotes: {
        bank: values.transfersNotesBank,
        branch: values.transfersNotesBranch
      },
      paymentsNotes: {
        gateway: values.paymentsNotesGateway,
        mode: values.paymentsNotesMode
      },
      settlementId: values.settlementId,
      settledAt: values.settledAt,
      settlementUtr: values.settlementUtr,
      arn: values.arn,
      additionalUtr: values.additionalUtr
    };

    addTopupSettlement(requestBody, (res: any) => {
      setLoading(false);
      if (res?.data?.success) {
        actions.resetForm();
        props?.onHide();
        props?.onSuccess?.();
      }
    });
  };

  return (
    <Formik
      initialValues={init}
      onSubmit={handleSubmit}
      validationSchema={TopupSettlementSchema}
      enableReinitialize>
      {(formikProps: FormikProps<any>) => {
        return (
          <Container fluid as={Form} className='p-0'>
            <Row className='mb-4'>
              <Col lg={6}>
                <OATextField name='entityId' label='Entity ID' required />
              </Col>
              <Col lg={6}>
                <OATextField name='transactionEntity' label='Transaction Entity' required />
              </Col>
            </Row>

            <Row className='mb-4'>
              <Col lg={6}>
                <OADatePickerField
                  name='entityCreatedAt'
                  label='Entity Created At'
                  maxDate={new Date()}
                  required
                />
              </Col>
            </Row>

            <Row className='mb-4'>
              <Col lg={6}>
                <OATextField name='amount' label='Amount' type='number' required />
              </Col>
              <Col lg={6}>
                <OATextField name='paymentMethod' label='Payment Method' />
              </Col>
            </Row>

            <h5 className='mb-3 mt-4'>Transfer Notes</h5>
            <Row className='mb-4'>
              <Col lg={6}>
                <OATextField name='transfersNotesBank' label='Bank' />
              </Col>
              <Col lg={6}>
                <OATextField name='transfersNotesBranch' label='Branch' />
              </Col>
            </Row>

            <h5 className='mb-3 mt-4'>Payment Notes</h5>
            <Row className='mb-4'>
              <Col lg={6}>
                <OATextField name='paymentsNotesGateway' label='Gateway' />
              </Col>
              <Col lg={6}>
                <OATextField name='paymentsNotesMode' label='Mode' />
              </Col>
            </Row>

            <h5 className='mb-3 mt-4'>Settlement Details</h5>
            <Row className='mb-4'>
              <Col lg={6}>
                <OATextField name='settlementId' label='Settlement ID' />
              </Col>
              <Col lg={6}>
                <OATextField name='settledAt' label='Settled At (ISO 8601)' placeholder='2024-12-15T14:00:00Z' />
              </Col>
            </Row>

            <Row className='mb-4'>
              <Col lg={6}>
                <OATextField name='settlementUtr' label='Settlement UTR' />
              </Col>
              <Col lg={6}>
                <OATextField name='arn' label='ARN' />
              </Col>
            </Row>

            <Row className='mb-4'>
              <Col lg={6}>
                <OATextField name='additionalUtr' label='Additional UTR' />
              </Col>
            </Row>

            <div className='d-flex justify-content-end mt-5'>
              <OAButton
                className='btn-light-primary btn-active-light-primary me-3'
                label='Cancel'
                onClick={() => {
                  formikProps?.resetForm();
                  props?.onHide();
                }}
              />
              <OAButton
                type='submit'
                disabled={!formikProps?.dirty || !formikProps?.isValid}
                isLoading={loading}
                loadingText='Adding Settlement...'
                label='Add Topup Settlement'
              />
            </div>
          </Container>
        );
      }}
    </Formik>
  );
}